import type { CarePlan, ServiceRequest } from '@medplum/fhirtypes';
import React, { useMemo } from 'react';

import { Stack, Tag, Text, cn } from '@eluve/components';
import { formatFhirDate } from '@eluve/fhir-renderer';

import { cadenceRows, visitTimeline } from '../plan-list';

/**
 * The visit-schedule half of the Care Plans section (Rev7): the projected
 * visit timeline (plan-shapes' ladder — encounters-on-plan is out of scope,
 * so every visit is a projection, past ones included) beside the
 * cadence-by-phase table from the plan's session-frequency snapshot.
 * Renders nothing when the plan carries no cadence.
 */
export type VisitScheduleCardProps = {
  plan: CarePlan;
  serviceRequests: ServiceRequest[];
  todayIsoDate: string;
};

export const VisitScheduleCard: React.FC<VisitScheduleCardProps> = ({
  plan,
  serviceRequests,
  todayIsoDate,
}) => {
  const cadence = useMemo(() => cadenceRows(plan), [plan]);
  const timeline = useMemo(
    () => visitTimeline(plan, serviceRequests, todayIsoDate),
    [plan, serviceRequests, todayIsoDate],
  );
  if (!cadence && !timeline) return null;
  const visits = timeline?.visits;
  const nextIndex = timeline?.nextIndex ?? -1;

  return (
    <section
      data-testid="visit-schedule-card"
      className="rounded-lg bg-bg-primary shadow-xs ring-1 ring-black/5"
    >
      <Stack
        direction="horizontal"
        gap={2}
        align="center"
        justify="between"
        className="h-12 px-4"
      >
        <Text inline size="sm" weight="semibold">
          Visit Schedule
        </Text>
      </Stack>
      <div
        className={cn(
          'grid p-4 pt-0',
          visits && cadence
            ? 'grid-cols-2 divide-x divide-border-primary'
            : 'grid-cols-1',
        )}
      >
        {visits && (
          <div className={cn(cadence && 'pr-6')} data-testid="visit-timeline">
            {visits.map((visit, index) => (
              <div key={visit.number} className="flex gap-3">
                <div className="flex w-2 shrink-0 flex-col items-center">
                  <span
                    aria-hidden
                    className={cn(
                      'mb-px h-2.5 w-px shrink-0',
                      index > 0 &&
                        (index <= nextIndex
                          ? 'bg-text-primary'
                          : 'bg-border-primary'),
                    )}
                  />
                  <span
                    aria-hidden
                    className={cn(
                      'size-1.75 shrink-0 rounded-full',
                      index < nextIndex
                        ? 'bg-text-primary'
                        : index === nextIndex
                          ? 'bg-bg-indigo-strong-primary'
                          : 'bg-border-primary',
                    )}
                  />
                  {index < visits.length - 1 && (
                    <span
                      aria-hidden
                      className={cn(
                        'mt-px w-px flex-1',
                        index < nextIndex
                          ? 'bg-text-primary'
                          : 'bg-border-primary',
                      )}
                    />
                  )}
                </div>
                <div
                  className={cn(
                    'flex min-w-0 flex-1 items-baseline gap-2 pt-1',
                    index < visits.length - 1 && 'pb-2',
                  )}
                >
                  <Text
                    inline
                    size="xs"
                    color="tertiary"
                    className="w-19 shrink-0 whitespace-nowrap tabular-nums"
                  >
                    {formatFhirDate(visit.date) ?? '—'}
                  </Text>
                  <Text inline size="sm" weight="medium">
                    {visit.gate ? 'Reassessment' : 'Visit'}
                  </Text>
                  {index === nextIndex && (
                    <Tag pill subtle size="xs" color="insights" label="Next" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {cadence && (
          <div className={cn(visits && 'pl-6')} data-testid="cadence-table">
            {cadence.map((row) => (
              <Stack
                key={row.weeksLabel}
                direction="horizontal"
                gap={4}
                align="center"
                justify="between"
                className="py-1"
              >
                <Text inline size="xs" color="tertiary">
                  {row.weeksLabel}
                </Text>
                <Text inline size="sm" weight="medium">
                  {row.cadence
                    .replace(/\bwks\b/g, 'weeks')
                    .replace(/\bwk\b/g, 'week')
                    .replace(/\s*\/\s*/, ' / ')}
                </Text>
              </Stack>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
